import React, { useEffect, useMemo, useState } from 'react';

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Link, Stack, Typography } from '@mui/material';

import {
  SavedRouteAlert,
  SavedRouteAlertCreateInput,
  useGetSavedRouteAlertsQuery,
} from 'state/emailAlertsSlices';
import { useGetProjectQuery } from 'state/projectSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import { displayFriendlyDayMonthYear } from 'features/common/datetimeUtils';
import { TaskBarTabPanel } from 'features/task_bar/TaskBarTabs';

import { NotificationsPanel } from './NotificationsPanel';
import { AlertTab, TABS } from './alertConstants';
import { AlertTriggersField } from './alertFields/AlertTriggersField';
import { ManageRoutes } from './alertFields/ManageRoutes';
import { Recipients } from './alertFields/Recipients';
import { RouteMapDisplay } from './alertFields/RouteMapDisplay';
import { MetricType, arraysEqual, formatAlertName } from './alertHelpers';
import { AlertTabs } from './components/AlertTabs';
import { DeleteAlertPopup } from './components/DeleteAlertPopup';
import { useNotificationProject } from './useNotificationProject';

import styles from './Alert.module.css';

interface AlertFormProps {
  alert?: SavedRouteAlert;
  onSave: (data: SavedRouteAlertCreateInput) => void;
  onDelete: () => void;
  onCancel: () => void;
}

const EMPTY_DEFAULT_ALERT: SavedRouteAlertCreateInput = {
  metric_type: MetricType.PERCENT_SLOWDOWN,
  metric_criteria: 0.5,
  typical_year_num: null,
  typical_month_num: null,
  schedule_day_types: [],
  schedule_day_hours: [],
  has_resolution_email: false,
  routes: [],
  recipients: [],
};

export function AlertForm({
  alert,
  onDelete,
  onSave,
  onCancel,
}: AlertFormProps) {
  const { projectSlug, navigateTo } = useNotificationProject();
  const { data: projectData } = useGetProjectQuery(projectSlug!);
  const { data: existingAlerts } = useGetSavedRouteAlertsQuery(projectSlug!, {
    skip: !projectSlug,
  });

  const [activeTab, setActiveTab] = useState<AlertTab>(AlertTab.Triggers);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [completedTabs, setCompletedTabs] = useState<AlertTab[]>([]);
  const [formState, setFormState] = useState<
    SavedRouteAlert | SavedRouteAlertCreateInput
  >(() => {
    return alert || EMPTY_DEFAULT_ALERT;
  });
  const [formDirty, setFormDirty] = useState(false);

  useEffect(() => {
    setFormDirty(false);
  }, [alert?.hash_id]);

  const updateField = (field: keyof SavedRouteAlertCreateInput, value: any) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (alert) setFormDirty(true);
  };
  const currentTabIndex = TABS.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === TABS.length - 1;
  const isCreateMode = !alert;
  const availableEmails = projectData?.subscribed_emails || [];

  const routeIds = useMemo(
    () => formState.routes.map((route) => route.id),
    [formState.routes],
  );
  const routeNames = useMemo(
    () => formState.routes.map((route) => route.name),
    [formState.routes],
  );

  const availableRouteNames =
    projectData?.saved_routes?.map((route) => route.name) || [];

  const availableRouteIds =
    projectData?.saved_routes?.map((route) => route.id) || [];

  const handleRoutesChange = (routeNames: string[]) => {
    const routeObjects = routeNames.map((routeName) => {
      const route = projectData?.saved_routes?.find(
        (r) => r.name === routeName,
      );
      return route ? { id: route.id, name: route.name } : null;
    });
    updateField('routes', routeObjects);
  };

  const handleTabChange = (tab: AlertTab) => {
    setActiveTab(tab);
  };

  const handleNext = () => {
    if (!isLastTab) {
      const nextTab = TABS[currentTabIndex + 1];
      if (isCreateMode && !completedTabs.includes(activeTab)) {
        setCompletedTabs((prev) => [...prev, activeTab]);
      }
      setActiveTab(nextTab);
    }
  };

  const handleBack = () => {
    if (!isFirstTab) {
      setActiveTab(TABS[currentTabIndex - 1]);
    }
  };

  const duplicateAlert = useMemo(() => {
    if (!existingAlerts) return null;

    return existingAlerts.find((existingAlert) => {
      if (existingAlert.hash_id == alert?.hash_id) return false; // when editing, we don't want to compare the alert with itself
      if (existingAlert.metric_criteria !== formState.metric_criteria) {
        return false;
      }
      if (existingAlert.typical_month_num !== formState.typical_month_num) {
        return false;
      }
      if (
        !arraysEqual(
          existingAlert.schedule_day_types,
          formState.schedule_day_types,
        )
      ) {
        return false;
      }
      if (
        !arraysEqual(
          existingAlert.schedule_day_hours,
          formState.schedule_day_hours,
        )
      ) {
        return false;
      }
      if (
        !arraysEqual(
          existingAlert.routes.map((r) => r.id),
          formState.routes.map((r) => r.id),
        )
      ) {
        return false;
      }

      return true;
    });
  }, [
    isCreateMode,
    existingAlerts,
    formState.metric_criteria,
    formState.typical_month_num,
    formState.schedule_day_types,
    formState.schedule_day_hours,
    formState.routes,
  ]);

  const triggersTabComplete = useMemo(() => {
    if (formState.metric_criteria <= 0) return false;
    if (
      formState.typical_year_num === null ||
      formState.typical_month_num === null
    ) {
      return false;
    }
    if (formState.schedule_day_types.length === 0) return false;
    if (formState.schedule_day_hours.length === 0) return false;
    return true;
  }, [formState]);

  const routesTabComplete = formState.routes.length > 0;

  const isFormValid = triggersTabComplete && routesTabComplete;

  const canAdvanceFromCurrentTab = useMemo(() => {
    if (activeTab === AlertTab.Triggers) return triggersTabComplete;
    if (activeTab === AlertTab.Routes) return routesTabComplete;
    return true;
  }, [activeTab, triggersTabComplete, routesTabComplete]);

  const canCreate = isFormValid && !duplicateAlert;
  const canSave = isFormValid && formDirty && !duplicateAlert;

  const canNavigateTo = (tab: AlertTab) => {
    if (!isCreateMode) return true;
    const targetIdx = TABS.indexOf(tab);
    const currentIdx = TABS.indexOf(activeTab);
    if (targetIdx <= currentIdx) return true;
    if (tab === AlertTab.Routes) return triggersTabComplete;
    if (tab === AlertTab.Recipients)
      return triggersTabComplete && routesTabComplete;
    return true;
  };

  const handleSave = () => {
    const formData: SavedRouteAlertCreateInput = {
      ...formState,
      is_enabled: alert?.is_enabled ?? true,
    };
    onSave(formData);
    setFormDirty(false);
  };

  const handleDeleteClick = () => {
    setIsDeletePopupOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete();
    setIsDeletePopupOpen(false);
  };

  const handleDeleteCancel = () => {
    setIsDeletePopupOpen(false);
  };

  const alertName = formatAlertName(
    formState.metric_type,
    formState.metric_criteria,
  );

  return (
    <NotificationsPanel>
      <Stack gap={0.25} height="100%">
        <Stack className={styles.header}>
          <Box className={styles.breadcrumbs}>
            <Typography variant="h3">
              {alert ? 'Edit Alert' : 'Create Alert'}
            </Typography>
            <Box className={styles.chipTitle}>
              <Typography variant="h3">{alertName}</Typography>
            </Box>
          </Box>
          {alert && (
            <Button
              variant="text"
              startIcon={<DeleteIcon className={styles.buttonIcon} />}
              onClick={handleDeleteClick}
            >
              <Typography variant="h4" className={styles.whiteButtonText}>
                Delete
              </Typography>
            </Button>
          )}
        </Stack>
        {alert && alert.updated_by ? (
          <Typography variant="caption" className={styles.alertCaption}>
            Last updated by {alert.updated_by} on{' '}
            {displayFriendlyDayMonthYear(alert.updated_on)}
          </Typography>
        ) : null}

        {duplicateAlert && (
          <Stack
            direction="row"
            spacing={0.625}
            className={styles.duplicateAlertContainer}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FontAwesomeIcon
                type="regular"
                name="triangle-exclamation"
                gold
                sx={{ padding: 0 }}
              />
              <Typography variant="caption">
                An alert with this configuration already exists. Please update
                your settings.
              </Typography>
            </Stack>
            <Link
              component="button"
              onClick={() => navigateTo.viewAlert(duplicateAlert.hash_id)}
              className={styles.viewExistingAlertLink}
            >
              View existing alert
            </Link>
          </Stack>
        )}

        <AlertTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isCreateMode={isCreateMode}
          completedTabs={completedTabs}
          canNavigateTo={isCreateMode ? canNavigateTo : undefined}
        >
          <TaskBarTabPanel value={activeTab} index={AlertTab.Triggers}>
            <AlertTriggersField
              mode="edit"
              metricThresholdLocked={!!alert}
              metricType={formState.metric_type}
              metricCriteria={formState.metric_criteria}
              typicalYearNum={formState.typical_year_num}
              typicalMonthNum={formState.typical_month_num}
              scheduleDayTypes={formState.schedule_day_types}
              scheduleDayHours={formState.schedule_day_hours}
              hasResolutionEmail={formState.has_resolution_email}
              onMetricCriteriaChange={(value) =>
                updateField('metric_criteria', value)
              }
              onTypicalYearChange={(value) =>
                updateField('typical_year_num', value)
              }
              onTypicalMonthChange={(value) =>
                updateField('typical_month_num', value)
              }
              onDayTypesChange={(value) =>
                updateField('schedule_day_types', value)
              }
              onDayHoursChange={(value) =>
                updateField('schedule_day_hours', value)
              }
              onResolutionEmailChange={(value) =>
                updateField('has_resolution_email', value)
              }
            />
          </TaskBarTabPanel>
          <TaskBarTabPanel
            value={activeTab}
            index={AlertTab.Routes}
            extraClass={styles.fullHeight}
          >
            <Stack direction="row" spacing={0.5} width="100%" height="100%">
              <Box flex={1}>
                <ManageRoutes
                  mode="edit"
                  routes={routeNames}
                  onRoutesChange={handleRoutesChange}
                  availableRoutes={availableRouteNames}
                />
              </Box>
              <Box flex={1}>
                <RouteMapDisplay
                  selectedRoutes={routeIds}
                  availableRoutes={availableRouteIds}
                  showRouteCount
                />
              </Box>
            </Stack>
          </TaskBarTabPanel>
          <TaskBarTabPanel
            value={activeTab}
            index={AlertTab.Recipients}
            extraClass={styles.fullHeight}
          >
            <Recipients
              mode="edit"
              recipients={formState.recipients}
              onRecipientsChange={(value) => updateField('recipients', value)}
              availableEmails={availableEmails}
            />
          </TaskBarTabPanel>
        </AlertTabs>

        {alert ? (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 'auto', pt: 1, borderTop: 1, borderColor: 'divider' }}
          >
            <StlButton
              variant="outlined"
              handleClick={onCancel}
              value="Cancel"
              width="6rem"
            />

            <StlButton
              variant="contained"
              handleClick={handleSave}
              value="Save"
              width="6rem"
              isActive={canSave}
            />
          </Stack>
        ) : (
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mt: 'auto', pt: 1, borderTop: 1, borderColor: 'divider' }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              startIcon={<ArrowBack />}
            >
              Cancel
            </Button>
            <Stack direction="row" spacing={2}>
              <StlButton
                variant="outlined"
                handleClick={handleBack}
                value="Back"
                width="6rem"
                isActive={!isFirstTab}
              />
              {isLastTab ? (
                <StlButton
                  variant="contained"
                  handleClick={handleSave}
                  value="Create"
                  width="6rem"
                  isActive={canCreate}
                />
              ) : (
                <StlButton
                  variant="contained"
                  handleClick={handleNext}
                  value="Next"
                  width="6rem"
                  icon={<ArrowForward />}
                  isActive={canAdvanceFromCurrentTab}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
      <DeleteAlertPopup
        isOpen={isDeletePopupOpen}
        alertName={alertName}
        routes={routeNames}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </NotificationsPanel>
  );
}
